import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  InputGroup,
  InputLeftElement,
  Flex,
  Text,
  TableContainer,
  VStack,
  Select,
  Button,
  HStack,
  useToast,
  Spinner,
  Badge,
  useBreakpointValue,
} from "@chakra-ui/react";
import {
  SearchIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@chakra-ui/icons";
import { useState, useEffect } from "react";
import axios from "axios";
import Navbar from "../../Components/Navbar/Navbar";
import Sidebar from "../../Components/Sidebar";
import Pagination from "../../Components/Common/Pagination";
import { api_url } from "../../url";

const TransactionHistory = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");

  const toast = useToast();
  const SIDEBAR_WIDTH = "240px";
  const isMobile = useBreakpointValue({ base: true, md: false });

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${api_url}/transactions/all`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page,
          limit,
          search,
          type,
          status,
        },
      });
      if (response.data.success) {
        setTransactions(response.data.transactions);
        setTotalPages(response.data.pagination.pages);
        setTotalTransactions(response.data.pagination.total);
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
      toast({
        title: "Error fetching transactions.",
        description: error.response?.data?.message || "Something went wrong.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchTransactions();
    }, 500); // Debounce search
    return () => clearTimeout(timeoutId);
  }, [page, search, type, status]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1); // Reset to first page on search
  };

  const handleTypeChange = (e) => {
    setType(e.target.value);
    setPage(1);
  };

  const handleStatusChange = (e) => {
    setStatus(e.target.value);
    setPage(1);
  };

  const handlePrevPage = () => {
    if (page > 1) setPage(page - 1);
  };

  const handleNextPage = () => {
    if (page < totalPages) setPage(page + 1);
  };

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "success":
        return "green";
      case "pending":
        return "orange";
      case "failed":
        return "red";
      case "refunded":
        return "blue";
      case "cancelled":
        return "gray";
      default:
        return "gray";
    }
  };

  return (
    <>
      <Navbar />
      <Sidebar />
      <Box
        p={{ base: 4, md: 8 }}
        bg="gray.50"
        minH="100vh"
        mt="64px"
        ml={{ base: 0, md: SIDEBAR_WIDTH }}
      >
        <Flex
          mb={6}
          justify="space-between"
          align={{ base: "stretch", md: "center" }}
          direction={{ base: "column", md: "row" }}
          gap={4}
        >
          <Text fontSize="2xl" fontWeight="bold" color="gray.800">
            Transaction History
          </Text>

          <Flex gap={4} wrap="wrap" justify="flex-end" flex={1}>
            <InputGroup maxW={{ base: "100%", md: "300px" }}>
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                bg="white"
                placeholder="Search by ID, User..."
                value={search}
                onChange={handleSearch}
                borderRadius="md"
              />
            </InputGroup>
            <Select
              placeholder="All Types"
              w={{ base: "100%", md: "150px" }}
              bg="white"
              value={type}
              onChange={handleTypeChange}
            >
              <option value="deposit">Deposit</option>
              <option value="withdraw">Withdraw</option>
              <option value="bet">Bet</option>
              <option value="win">Win</option>
              <option value="refund">Refund</option>
              <option value="transfer">Transfer</option>
              <option value="bonus">Bonus</option>
            </Select>
            <Select
              placeholder="All Status"
              w={{ base: "100%", md: "150px" }}
              bg="white"
              value={status}
              onChange={handleStatusChange}
            >
              <option value="success">Success</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
              <option value="cancelled">Cancelled</option>
            </Select>
          </Flex>
        </Flex>

        {loading ? (
          <Flex justify="center" align="center" h="200px">
            <Spinner size="xl" color="blue.500" />
          </Flex>
        ) : (
          <>
            {isMobile ? (
              <VStack spacing={4} align="stretch">
                {transactions.map((txn) => (
                  <Box
                    key={txn._id}
                    bg="white"
                    p={4}
                    borderRadius="lg"
                    boxShadow="sm"
                    border="1px solid #e2e8f0"
                  >
                    <Flex justify="space-between" mb={2}>
                      <Text fontWeight="600" fontSize="sm">
                        Txn ID
                      </Text>
                      <Text fontSize="sm" isTruncated maxW="150px">
                        {txn.transactionId}
                      </Text>
                    </Flex>
                    <Flex justify="space-between" mb={2}>
                      <Text fontWeight="600" fontSize="sm">
                        User
                      </Text>
                      <Text fontSize="sm">
                        {txn.user?.username || "Unknown"}
                      </Text>
                    </Flex>
                    <Flex justify="space-between" mb={2}>
                      <Text fontWeight="600" fontSize="sm">
                        Amount
                      </Text>
                      <Text
                        fontWeight="bold"
                        color={
                          txn.action === "credit" ? "green.500" : "red.500"
                        }
                        fontSize="sm"
                      >
                        {txn.action === "credit" ? "" : "-"}{txn.currency === "INR" ? "₹" : txn.currency}
                        {txn.amount}
                      </Text>
                    </Flex>
                    <Flex justify="space-between" mb={2}>
                      <Text fontWeight="600" fontSize="sm">
                        Type
                      </Text>
                      <Badge
                        colorScheme={txn.action === "credit" ? "green" : "red"}
                      >
                        {txn.actionType}
                      </Badge>
                    </Flex>
                    <Flex justify="space-between" mb={2}>
                      <Text fontWeight="600" fontSize="sm">
                        Status
                      </Text>
                      <Badge colorScheme={getStatusColor(txn.status)}>
                        {txn.status}
                      </Badge>
                    </Flex>
                    <Flex
                      justify="space-between"
                      pt={2}
                      borderTop="1px solid #f0f2f5"
                    >
                      <Text fontWeight="600" fontSize="sm">
                        Time
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        {new Date(txn.createdAt).toLocaleString()}
                      </Text>
                    </Flex>
                  </Box>
                ))}
              </VStack>
            ) : (
              <Box
                bg="white"
                borderRadius="lg"
                overflow="hidden"
                boxShadow="sm"
                border="1px solid #e2e8f0"
              >
                <TableContainer>
                  <Table variant="simple">
                    <Thead bg="gray.100">
                      <Tr>
                        <Th>Transaction ID</Th>
                        <Th>User</Th>
                        <Th>Action</Th>
                        <Th>Type</Th>
                        <Th>Amount</Th>
                        <Th>Status</Th>
                        <Th>Description</Th>
                        <Th>Time</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {transactions.length > 0 ? (
                        transactions.map((txn) => (
                          <Tr key={txn._id} _hover={{ bg: "gray.50" }}>
                            <Td fontSize="sm" fontWeight="medium">
                              {txn.transactionId}
                            </Td>
                            <Td fontSize="sm">
                              {txn.user?.username || "Unknown"}
                            </Td>
                            <Td>
                              <Badge
                                colorScheme={
                                  txn.action === "credit" ? "green" : "red"
                                }
                              >
                                {txn.action?.toUpperCase()}
                              </Badge>
                            </Td>
                            <Td fontSize="sm" textTransform="capitalize">
                              {txn.actionType}
                            </Td>
                            <Td
                              fontWeight="bold"
                              color={
                                txn.action === "credit"
                                  ? "green.600"
                                  : "red.600"
                              }
                            >
                              {txn.action === "credit" ? "" : "-"}{txn.currency === "INR" ? "₹" : txn.currency}{txn.amount}
                            </Td>
                            <Td>
                              <Badge colorScheme={getStatusColor(txn.status)}>
                                {txn.status}
                              </Badge>
                            </Td>
                            <Td
                              fontSize="sm"
                              maxW="200px"
                              isTruncated
                              title={txn.description}
                            >
                              {txn.description}
                            </Td>
                            <Td fontSize="sm" color="gray.600">
                              {new Date(txn.createdAt).toLocaleString()}
                            </Td>
                          </Tr>
                        ))
                      ) : (
                        <Tr>
                          <Td
                            colSpan={8}
                            textAlign="center"
                            py={6}
                            color="gray.500"
                          >
                            No transactions found.
                          </Td>
                        </Tr>
                      )}
                    </Tbody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Pagination */}
            <Pagination 
              currentPage={page}
              totalPages={totalPages}
              totalEntries={totalTransactions}
              limit={limit}
              onPageChange={setPage}
              onLimitChange={handleLimitChange}
            />
          </>
        )}
      </Box>
    </>
  );
};

export default TransactionHistory;
